import React, { useState } from 'react'
import { Outlet, useLocation, Link, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  BarChart3,
  Truck,
  FileText,
  Building2,
  MapPin,
  DollarSign,
  Users,
  Menu,
  X,
  ChevronDown,
  Home,
  Bell,
  Search,
  TrendingUp,
  Settings,
  Upload
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useTheme } from '../contexts/ThemeContext'
import { useModuleConfig } from '../contexts/ModuleConfigContext'
import { useOwner } from '../contexts/OwnerContext'
import ThemeToggle from '../components/ThemeToggle'
import LogoutButton from '../components/LogoutButton'
import Footer from '../components/Footer'

interface MenuItem {
  id: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  href: string
  subItems?: MenuItem[]
}

const menuItems: MenuItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: Home,
    href: '/'
  },
  {
    id: 'cte',
    label: 'CTEs',
    icon: Truck,
    href: '/ctes',
    subItems: [
      { id: 'ctes-analysis', label: '📊 Insights Inteligentes', icon: BarChart3, href: '/ctes/analysis' },
      // { id: 'ctes-overview', label: 'Visão Geral', icon: BarChart3, href: '/ctes/overview' },
      // { id: 'ctes-routes', label: 'Rotas', icon: MapPin, href: '/ctes/routes' }
    ]
  },
  {
    id: 'nfe',
    label: 'NFes',
    icon: FileText,
    href: '/nfes',
    subItems: [
      { id: 'nfes-analysis', label: '📊 Insights Inteligentes', icon: BarChart3, href: '/nfes/analysis' },
      // { id: 'nfes-overview', label: 'Visão Geral', icon: BarChart3, href: '/nfes/overview' },
      // { id: 'nfes-fiscal', label: 'Fiscal', icon: FileText, href: '/nfes/fiscal' }
    ]
  },
  {
    id: 'cd',
    label: 'Centros de Distribuição',
    icon: Building2,
    href: '/distribution-centers',
    subItems: [
      { id: 'cd-analysis', label: 'Análise de CD', icon: BarChart3, href: '/distribution-centers/analysis' },
      // { id: 'cd-performance', label: 'Performance', icon: BarChart3, href: '/distribution-centers/performance' },
      // { id: 'cd-capacity', label: 'Capacidade', icon: Building2, href: '/distribution-centers/capacity' }
    ]
  },
  {
    id: 'logistics',
    label: 'Malha Logística',
    icon: MapPin,
    href: '/logistics',
    subItems: [
      { id: 'sales-force-analysis', label: 'Análise da Força de Venda', icon: TrendingUp, href: '/logistics/sales-force-analysis' },
      { id: 'freight-analysis', label: 'Análise de Fretes', icon: DollarSign, href: '/logistics/freight-analysis' },
      { id: 'vehicle-freight-analysis', label: 'Análise de Frete por Veículo', icon: Truck, href: '/logistics/vehicle-freight-analysis' },
      // { id: 'logistics-routes', label: 'Rotas', icon: MapPin, href: '/logistics/routes' },
      // { id: 'logistics-network', label: 'Rede', icon: MapPin, href: '/logistics/network' },
      // { id: 'logistics-optimization', label: 'Otimização', icon: BarChart3, href: '/logistics/optimization' }
    ]
  },
  {
    id: 'costs',
    label: 'Análise de Custos',
    icon: DollarSign,
    href: '/costs',
    subItems: [
      // { id: 'costs-overview', label: 'Visão Geral', icon: BarChart3, href: '/costs/overview' },
      // { id: 'costs-breakdown', label: 'Breakdown', icon: DollarSign, href: '/costs/breakdown' },
      { id: 'costs-trends', label: 'Tendências', icon: BarChart3, href: '/costs/trends' }
    ]
  },
  {
    id: 'clients',
    label: 'Carteira de Clientes',
    icon: Users,
    href: '/clients',
    subItems: [
      { id: 'clients-overview', label: 'Visão Geral', icon: BarChart3, href: '/clients/overview' },
      // { id: 'clients-segments', label: 'Segmentos', icon: Users, href: '/clients/segments' },
      // { id: 'clients-performance', label: 'Performance', icon: BarChart3, href: '/clients/performance' }
    ]
  }
]

const MainLayout: React.FC = () => {
  const { theme } = useTheme()
  const { isModuleEnabled } = useModuleConfig()
  const { headers } = useOwner()
  const location = useLocation()
  const navigate = useNavigate()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')

  const handleMenuToggle = () => {
    setIsMenuOpen(!isMenuOpen)
  }

  const handleSubmenuToggle = (itemId: string) => {
    setActiveSubmenu(activeSubmenu === itemId ? null : itemId)
  }

  const isActiveRoute = (href: string) => {
    if (href === '/') {
      return location.pathname === '/' || location.pathname === '/dashboard'
    }
    return location.pathname.startsWith(href)
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`)
      setSearchQuery('')
    }
  }

  // Filter menu items based on module configuration
  const getFilteredMenuItems = () => {
    return menuItems.filter(item => {
      // Dashboard is always visible
      if (item.id === 'dashboard') return true
      
      // Check if module is enabled (only for valid module keys)
      if (['cte', 'nfe', 'cd', 'logistics', 'costs', 'clients'].includes(item.id)) {
        return isModuleEnabled(item.id as any)
      }
      
      // For any other items, show by default
      return true
    })
  }

  const filteredMenuItems = getFilteredMenuItems()

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
  }

  return (
    <div 
      className="min-h-screen w-full transition-colors duration-300 flex flex-col"
      style={{ backgroundColor: 'var(--bg-primary)' }}
    >
      {/* Header */}
      <header 
        className="shadow-sm border-b transition-colors duration-300 flex-shrink-0"
        style={{ 
          backgroundColor: 'var(--bg-card)', 
          borderColor: 'var(--border-primary)' 
        }}
      >
        <div className="w-full px-8">
          <div className="flex justify-between items-center h-24">
            {/* Logo */}
            <div className="flex items-center flex-shrink-0">
              <Link to="/" className="flex items-center space-x-3 hover:opacity-80 transition-opacity duration-200">
                <div className="w-12 h-12 flex items-center justify-center overflow-hidden">
                  <img 
                    src="/logo.png" 
                    alt="Lognex Insight Logo" 
                    className="w-12 h-12 object-contain transition-all duration-300"
                    style={{
                      filter: theme === 'dark' ? 'brightness(1.5) contrast(1.2)' : 'none'
                    }}
                  />
                </div>
                <div className="flex flex-col min-w-0 flex-shrink-0">
                  <h1 
                    className="text-4xl font-extrabold gradient-text text-left tracking-tight hidden md:block"
                    style={{
                      textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                      letterSpacing: '-0.025em'
                    }}
                  >
                    Lognex Insight
                  </h1>
                  {headers?.companyName && (
                    <p 
                      className="text-sm font-medium text-left hidden md:block whitespace-nowrap overflow-visible"
                      style={{
                        color: 'var(--text-secondary)',
                        marginTop: '4px',
                        maxWidth: 'none',
                        width: 'auto'
                      }}
                      title={headers.companyName} // Tooltip para ver o nome completo
                    >
                      {headers.companyName}
                    </p>
                  )}
                </div>
              </Link>
            </div>

            {/* Search Bar */}
            <div className="flex-1 max-w-lg mx-8">
              <form onSubmit={handleSearch}>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-5 w-5" style={{ color: 'var(--text-tertiary)' }} />
                  </div>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={handleSearchInputChange}
                    className="block w-full pl-10 pr-3 py-2 rounded-md leading-5 placeholder-gray-500 focus:outline-none focus:ring-2 transition-colors duration-300"
                    style={{
                      backgroundColor: '#f1f5f9',
                      border: '1px solid var(--border-primary)',
                      color: 'var(--text-primary)'
                    }}
                    placeholder="Buscar dados..."
                  />
                </div>
              </form>
            </div>

            {/* Right side actions */}
            <div className="flex items-center space-x-4">
              {headers?.company === '21679546000190' && (
                <Link
                  to="/settings"
                  className="p-2 transition-colors duration-300 hover:opacity-80"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  <Settings className="h-6 w-6" />
                </Link>
              )}
              <button
                className="p-2 transition-colors duration-300"
                style={{ color: 'var(--text-secondary)' }}
              >
                <Bell className="h-6 w-6" />
              </button>
              <ThemeToggle />
              <LogoutButton />
              <button
                onClick={handleMenuToggle}
                className="p-2 transition-colors duration-300 lg:hidden"
                style={{ color: 'var(--text-secondary)' }}
              >
                {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.nav
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="border-t lg:hidden transition-colors duration-300"
              style={{ 
                backgroundColor: 'var(--bg-card)', 
                borderColor: 'var(--border-primary)' 
              }}
            >
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="py-4 space-y-2">
                  {filteredMenuItems.map((item) => (
                    <div key={item.id}>
                      {item.id === 'dashboard' ? (
                        <Link
                          to={item.href}
                          onClick={() => setIsMenuOpen(false)}
                          className={cn(
                            "w-full flex items-center justify-between px-3 py-2 text-sm font-medium rounded-md transition-colors",
                            "hover:opacity-80 focus:outline-none focus:opacity-80"
                          )}
                          style={{
                            color: 'var(--text-primary)',
                            backgroundColor: 'transparent'
                          }}
                        >
                          <div className="flex items-center">
                            <div style={{ color: 'var(--text-secondary)' }}>
                              <item.icon className="h-5 w-5 mr-3" />
                            </div>
                            {item.label}
                          </div>
                          {item.subItems && (
                            <ChevronDown 
                              className={cn(
                                "h-4 w-4 transition-transform",
                                activeSubmenu === item.id && "rotate-180"
                              )}
                              style={{ color: 'var(--text-secondary)' }}
                            />
                          )}
                        </Link>
                      ) : (
                        <button
                          onClick={() => item.subItems ? handleSubmenuToggle(item.id) : null}
                          className={cn(
                            "w-full flex items-center justify-between px-3 py-2 text-sm font-medium rounded-md transition-colors",
                            "hover:opacity-80 focus:outline-none focus:opacity-80"
                          )}
                          style={{
                            color: 'var(--text-primary)',
                            backgroundColor: 'transparent'
                          }}
                        >
                          <div className="flex items-center">
                            <div style={{ color: 'var(--text-secondary)' }}>
                              <item.icon className="h-5 w-5 mr-3" />
                            </div>
                            {item.label}
                          </div>
                          {item.subItems && (
                            <ChevronDown 
                              className={cn(
                                "h-4 w-4 transition-transform",
                                activeSubmenu === item.id && "rotate-180"
                              )}
                              style={{ color: 'var(--text-secondary)' }}
                            />
                          )}
                        </button>
                      )}
                      
                      {/* Submenu */}
                      {item.subItems && (
                        <AnimatePresence>
                          {activeSubmenu === item.id && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.2 }}
                              className="ml-8 space-y-1"
                            >
                              {item.subItems.map((subItem, index) => (
                                <motion.div
                                  key={subItem.id}
                                  initial={{ opacity: 0, x: -20 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ delay: index * 0.1, duration: 0.2 }}
                                >
                                  <Link
                                    to={subItem.href}
                                    onClick={() => setIsMenuOpen(false)}
                                    className="flex items-center px-3 py-2 text-sm rounded-md transition-all duration-200 hover:scale-105"
                                    style={{
                                      color: 'var(--text-secondary)',
                                      backgroundColor: 'transparent'
                                    }}
                                  >
                                    <subItem.icon className="h-4 w-4 mr-3" />
                                    {subItem.label}
                                  </Link>
                                </motion.div>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </motion.nav>
          )}
        </AnimatePresence>

        {/* Desktop Navigation */}
        <nav 
          className="hidden lg:block border-t transition-colors duration-300 flex-shrink-0"
          style={{ 
            backgroundColor: 'var(--bg-secondary)', 
            borderColor: 'var(--border-primary)' 
          }}
        >
          <div className="w-full px-8">
            <div className="flex justify-between items-center w-full">
              {filteredMenuItems.map((item) => (
                <div key={item.id} className="relative group flex-1 text-center">
                  <div
                    onMouseEnter={() => setActiveSubmenu(item.id)}
                    onMouseLeave={() => setActiveSubmenu(null)}
                    className="relative"
                  >
                    {item.id === 'dashboard' ? (
                      <Link
                        to={item.href}
                        className="flex items-center justify-center px-2 py-6 text-sm font-medium transition-all duration-300 relative group/item whitespace-nowrap w-full"
                        style={{
                          color: isActiveRoute(item.href) ? 'var(--text-primary)' : 'var(--text-secondary)',
                          backgroundColor: isActiveRoute(item.href) ? 'var(--primary)' : 'transparent'
                        }}
                      >
                        <motion.div
                          className="flex items-center justify-center"
                          whileHover={{ scale: 1.02 }}
                          transition={{ duration: 0.2 }}
                        >
                          <item.icon className="h-5 w-5 mr-2 flex-shrink-0" />
                          <span className="text-center">{item.label}</span>
                          {item.subItems && (
                            <motion.div
                              animate={{ rotate: activeSubmenu === item.id ? 180 : 0 }}
                              transition={{ duration: 0.2 }}
                            >
                              <ChevronDown className="h-4 w-4 ml-1 flex-shrink-0" />
                            </motion.div>
                          )}
                        </motion.div>
                        {isActiveRoute(item.href) && (
                          <motion.div
                            layoutId="activeIndicator"
                            className="absolute bottom-0 left-0 right-0 h-1 rounded-t"
                            style={{ backgroundColor: 'var(--text-primary)' }}
                            initial={false}
                            transition={{ type: "spring", stiffness: 500, damping: 30 }}
                          />
                        )}
                      </Link>
                    ) : (
                      <div
                        className="flex items-center justify-center px-2 py-6 text-sm font-medium transition-all duration-300 relative group/item whitespace-nowrap w-full cursor-default"
                        style={{
                          color: isActiveRoute(item.href) ? 'var(--text-primary)' : 'var(--text-secondary)',
                          backgroundColor: isActiveRoute(item.href) ? 'var(--primary)' : 'transparent'
                        }}
                      >
                        <motion.div
                          className="flex items-center justify-center"
                          whileHover={{ scale: 1.02 }}
                          transition={{ duration: 0.2 }}
                        >
                          <item.icon className="h-5 w-5 mr-2 flex-shrink-0" />
                          <span className="text-center">{item.label}</span>
                          {item.subItems && (
                            <motion.div
                              animate={{ rotate: activeSubmenu === item.id ? 180 : 0 }}
                              transition={{ duration: 0.2 }}
                            >
                              <ChevronDown className="h-4 w-4 ml-1 flex-shrink-0" />
                            </motion.div>
                          )}
                        </motion.div>
                        {isActiveRoute(item.href) && (
                          <motion.div
                            layoutId="activeIndicator"
                            className="absolute bottom-0 left-0 right-0 h-1 rounded-t"
                            style={{ backgroundColor: 'var(--text-primary)' }}
                            initial={false}
                            transition={{ type: "spring", stiffness: 500, damping: 30 }}
                          />
                        )}
                      </div>
                    )}

                    {/* Desktop Submenu */}
                    {item.subItems && (
                      <motion.div
                        initial={{ opacity: 0, y: -10, scale: 0.95 }}
                        animate={{
                          opacity: activeSubmenu === item.id ? 1 : 0,
                          y: activeSubmenu === item.id ? 0 : -10,
                          scale: activeSubmenu === item.id ? 1 : 0.95
                        }}
                        transition={{
                          duration: 0.2,
                          ease: "easeOut"
                        }}
                        className={cn(
                          "absolute top-full left-1/2 transform -translate-x-1/2 w-80 shadow-xl border rounded-lg z-50 pointer-events-none",
                          activeSubmenu === item.id ? "pointer-events-auto" : ""
                        )}
                        style={{
                          backgroundColor: 'var(--bg-card)',
                          borderColor: 'var(--border-primary)',
                          boxShadow: 'var(--shadow-xl)'
                        }}
                      >
                      <div className="py-2">
                        {item.subItems.map((subItem, index) => (
                          <motion.div
                            key={subItem.id}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{
                              opacity: activeSubmenu === item.id ? 1 : 0,
                              x: activeSubmenu === item.id ? 0 : -10
                            }}
                            transition={{
                              duration: 0.15,
                              delay: index * 0.05,
                              ease: "easeOut"
                            }}
                          >
                            <Link
                              to={subItem.href}
                              className="flex items-center px-5 py-3 text-sm transition-all duration-200 hover:scale-105 hover:bg-opacity-10 whitespace-nowrap"
                              style={{
                                color: 'var(--text-primary)',
                                backgroundColor: 'transparent'
                              }}
                            >
                              <motion.div
                                whileHover={{ scale: 1.1 }}
                                transition={{ duration: 0.2 }}
                              >
                                <subItem.icon className="h-4 w-4 mr-3 flex-shrink-0" />
                              </motion.div>
                              <span className="font-medium">{subItem.label}</span>
                            </Link>
                          </motion.div>
                        ))}
                      </div>
                      </motion.div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </nav>
      </header>

      {/* Main Content */}
      <main className="flex-1 w-full min-h-0">
        <Outlet />
      </main>

      {/* Footer */}
      <Footer />
    </div>
  )
}

export default MainLayout
